import { createAsyncThunk } from '@reduxjs/toolkit';
import { partnershipsApi } from './partnershipsApi';
import { extractApiError } from '../../utils/errors';

export const fetchAvailablePartnerships = createAsyncThunk(
  'partnerships/fetchAvailable',
  async (_, { rejectWithValue }) => {
    try {
      const data = await partnershipsApi.getAvailablePartnerships();
      return data.partners || [];
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'Error al obtener alianzas disponibles'));
    }
  }
);

export const fetchPartnerships = createAsyncThunk(
  'partnerships/fetchAll',
  async (adminId, { getState, rejectWithValue }) => {
    try {
      const { filters } = getState().partnerships;
      const params = {
        page: filters.page,
        page_size: filters.rowsPerPage,
      };
      if (filters.search) params.search = filters.search;

      const data = await partnershipsApi.getPartnerships(adminId, params);
      return {
        results: data.results || [],
        count: data.count || 0
      };
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'Error al obtener las alianzas'));
    }
  }
);

/**
 * The same table, read as the internal team: every alliance of the platform.
 *
 * A different payload rather than a wider one — `fetchPartnerships` answers
 * "mine" and tells each row from the asking admin's side, which staff does not
 * have. Writes into the same slice: a session is either staff or it is not.
 */
export const fetchStaffPartnerships = createAsyncThunk(
  'partnerships/fetchStaffAll',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { filters } = getState().partnerships;
      const data = await partnershipsApi.getStaffPartnerships({
        page: filters.page,
        page_size: filters.rowsPerPage,
        ...(filters.search ? { search: filters.search } : {}),
      });

      return { results: data.results || [], count: data.count || 0 };
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'Error al obtener las alianzas de la plataforma'));
    }
  }
);

export const createPartnershipThunk = createAsyncThunk(
  'partnerships/create',
  async ({ adminId, partnershipData, isStaff }, { dispatch, rejectWithValue }) => {
    try {
      await partnershipsApi.createPartnership(partnershipData);
      // Awaited, so the action is still running until the list shows its result.
      await Promise.all([
        dispatch(isStaff ? fetchStaffPartnerships() : fetchPartnerships(adminId)),
        dispatch(fetchAvailablePartnerships()),
      ]);
      return true;
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'Error al crear la alianza'));
    }
  }
);

export const deletePartnershipThunk = createAsyncThunk(
  'partnerships/delete',
  async ({ adminId, partnershipId, isStaff }, { dispatch, rejectWithValue }) => {
    try {
      await partnershipsApi.deletePartnership(partnershipId);
      // Until the list comes back without it, the row is still there to click.
      await Promise.all([
        dispatch(isStaff ? fetchStaffPartnerships() : fetchPartnerships(adminId)),
        dispatch(fetchAvailablePartnerships()),
      ]);
      return partnershipId;
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'Error al eliminar la alianza'));
    }
  },
  // The row it removes, so only that row's button spins.
  { getPendingMeta: ({ arg }) => ({ pendingKey: arg.partnershipId }) }
);
